/**
 * /control/terminaciones · M-13.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "../../lib/api";
import { TER_ESTADOS, CAUSAL_LABEL, fmtFecha, fmtMXN } from "./_common";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { FileX2, Plus, FileDown, Check, Lock } from "lucide-react";

export function TerminacionesList() {
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        fideicomiso_id: "", causal: "cumplimiento_fines",
        fecha_cierre: "", dictamen: "",
    });
    const [fids, setFids] = useState([]);
    const nav = useNavigate();

    const load = () => api.get("/control/terminaciones").then(r => setItems(r.data));
    useEffect(() => {
        load();
        api.get("/fideicomisos").then(r => {
            const arr = Array.isArray(r.data) ? r.data : r.data.items || [];
            setFids(arr.map(f => ({ id: f.id, label: f.numero_fideicomiso || f.id })));
        }).catch(() => setFids([]));
    }, []);

    const crear = async () => {
        if (!form.fideicomiso_id || form.dictamen.length < 10) {
            toast.error("Fideicomiso y dictamen (≥10) requeridos"); return;
        }
        try {
            const now = new Date().toISOString();
            const r = await api.post("/control/terminaciones", {
                fideicomiso_id: form.fideicomiso_id,
                causal: form.causal,
                fecha_disparo: now,
                fecha_estimada_cierre: form.fecha_cierre
                    ? new Date(form.fecha_cierre).toISOString()
                    : new Date(Date.now() + 60 * 86400000).toISOString(),
                dictamen_juridico_resumen: form.dictamen,
            });
            toast.success(`Terminación iniciada · ${r.data.folio_terminacion}`);
            setOpen(false);
            nav(`/control/terminaciones/${r.data.id}`);
        } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    };

    return (
        <div className="space-y-6" data-testid="terminaciones-list">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="text-3xl font-serif font-semibold" style={{ fontFamily: "'Fraunces', serif" }}>
                        Terminaciones y Liquidación · M-13
                    </h1>
                    <p className="text-sm text-muted-foreground">Prelación en orden · 60 días naturales objetivo.</p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button className="bg-[color:var(--coral)] hover:bg-[color:var(--coral)]/90 text-white"
                            data-testid="btn-nueva-terminacion">
                            <Plus className="h-4 w-4 mr-1" /> Nueva terminación
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-xl">
                        <DialogHeader><DialogTitle>Nueva terminación</DialogTitle></DialogHeader>
                        <div className="space-y-3">
                            <div>
                                <label className="text-xs font-semibold">Fideicomiso</label>
                                <Select value={form.fideicomiso_id}
                                    onValueChange={v => setForm({ ...form, fideicomiso_id: v })}>
                                    <SelectTrigger><SelectValue placeholder="..." /></SelectTrigger>
                                    <SelectContent>
                                        {fids.map(f => <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="text-xs font-semibold">Causal</label>
                                <Select value={form.causal} onValueChange={v => setForm({ ...form, causal: v })}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(CAUSAL_LABEL).map(([k, v]) =>
                                            <SelectItem key={k} value={k}>{v}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="text-xs font-semibold">Fecha estimada cierre</label>
                                <Input type="date" value={form.fecha_cierre}
                                    onChange={e => setForm({ ...form, fecha_cierre: e.target.value })} />
                            </div>
                            <div>
                                <label className="text-xs font-semibold">Dictamen jurídico (≥10)</label>
                                <Textarea rows={3} value={form.dictamen}
                                    onChange={e => setForm({ ...form, dictamen: e.target.value })} />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                            <Button onClick={crear} data-testid="btn-crear-terminacion">Crear</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
            <Card>
                <CardContent className="p-0">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/40 text-xs uppercase">
                            <tr>
                                <th className="px-3 py-2 text-left">Folio</th>
                                <th className="px-3 py-2 text-left">Causal</th>
                                <th className="px-3 py-2 text-left">Estado</th>
                                <th className="px-3 py-2 text-left">Fecha disparo</th>
                                <th className="px-3 py-2 text-left">Estimada cierre</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.length === 0 && (
                                <tr><td colSpan={5} className="p-6 text-center text-muted-foreground">Sin terminaciones.</td></tr>
                            )}
                            {items.map(t => {
                                const cfg = TER_ESTADOS[t.estado] || { label: t.estado, cls: "" };
                                return (
                                    <tr key={t.id} className="border-t hover:bg-muted/20">
                                        <td className="px-3 py-2 font-mono text-xs">
                                            <Link to={`/control/terminaciones/${t.id}`} className="text-primary hover:underline">
                                                {t.folio_terminacion}
                                            </Link>
                                        </td>
                                        <td className="px-3 py-2 text-xs">{CAUSAL_LABEL[t.causal] || t.causal}</td>
                                        <td className="px-3 py-2"><Badge className={cfg.cls}>{cfg.label}</Badge></td>
                                        <td className="px-3 py-2 text-xs">{fmtFecha(t.fecha_disparo)}</td>
                                        <td className="px-3 py-2 text-xs">{fmtFecha(t.fecha_estimada_cierre)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </CardContent>
            </Card>
        </div>
    );
}


export function TerminacionDetalle() {
    const { id } = useParams();
    const [t, setT] = useState(null);
    const reload = () => api.get(`/control/terminaciones/${id}`).then(r => setT(r.data));
    useEffect(() => { reload(); }, [id]);
    if (!t) return <div className="p-6 text-muted-foreground">Cargando…</div>;

    const cfg = TER_ESTADOS[t.estado] || { label: t.estado, cls: "" };
    const prelPagados = t.prelacion_pagos.filter(p => p.pagado).length;
    const chkCompletos = t.checklist_terminacion.filter(c => c.completo).length;

    const pagar = async (orden) => {
        const monto = parseFloat(prompt(`Monto para orden ${orden}:`, "0") || "0");
        if (isNaN(monto) || monto < 0) return;
        const ref = prompt("Referencia:") || "";
        try {
            await api.post(
                `/control/terminaciones/${id}/pagar-item-prelacion/${orden}`,
                { monto_pagado_mxn: monto, referencia: ref,
                  fecha_pago: new Date().toISOString() },
            );
            toast.success(`Orden ${orden} pagada`);
            reload();
        } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    };
    const toggleChk = async (idx, actual) => {
        try {
            await api.post(
                `/control/terminaciones/${id}/marcar-checklist/${idx}`,
                null, { params: { completo: !actual } },
            );
            reload();
        } catch (e) { toast.error("Error"); }
    };
    const cerrar = async () => {
        try {
            await api.post(`/control/terminaciones/${id}/cerrar`);
            toast.success("Terminación cerrada · Acta generada");
            reload();
        } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    };
    const pdf = () => window.open(`${api.defaults.baseURL}/control/terminaciones/${id}/pdf`, "_blank");

    return (
        <div className="space-y-6" data-testid="terminacion-detalle">
            <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-serif font-semibold" style={{ fontFamily: "'Fraunces', serif" }}>
                    {t.folio_terminacion}
                </h1>
                <Badge className={cfg.cls}>{cfg.label}</Badge>
                <span className="text-sm text-muted-foreground">{CAUSAL_LABEL[t.causal]}</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="md:col-span-2">
                    <CardHeader><CardTitle className="text-base">Dictamen jurídico</CardTitle></CardHeader>
                    <CardContent className="text-sm">{t.dictamen_juridico_resumen}</CardContent>
                </Card>
                <Card>
                    <CardHeader><CardTitle className="text-base">Acciones</CardTitle></CardHeader>
                    <CardContent className="space-y-2">
                        {t.estado !== "cerrada" && (
                            <Button className="w-full bg-[color:var(--coral)] hover:bg-[color:var(--coral)]/90 text-white"
                                onClick={cerrar} data-testid="btn-cerrar-ter">
                                <Lock className="h-4 w-4 mr-1" /> Cerrar y finiquitar
                            </Button>
                        )}
                        {t.estado === "cerrada" && (
                            <Button className="w-full" variant="outline" onClick={pdf}
                                data-testid="btn-descargar-acta-cierre">
                                <FileDown className="h-4 w-4 mr-1" /> Acta de cierre
                            </Button>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Prelación visual */}
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-base">
                        Prelación de pagos · M-13 §4.2
                        <span className="text-sm text-muted-foreground font-normal ml-2">
                            {prelPagados}/{t.prelacion_pagos.length}
                        </span>
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    <table className="w-full text-sm" data-testid="tabla-prelacion">
                        <thead className="bg-muted/40 text-xs uppercase">
                            <tr>
                                <th className="px-3 py-2 text-left">Orden</th>
                                <th className="px-3 py-2 text-left">Concepto</th>
                                <th className="px-3 py-2 text-right">Monto MXN</th>
                                <th className="px-3 py-2 text-center">Pagado</th>
                                <th className="px-3 py-2 text-right">Acción</th>
                            </tr>
                        </thead>
                        <tbody>
                            {t.prelacion_pagos.map(p => (
                                <tr key={p.orden} className="border-t">
                                    <td className="px-3 py-2 font-bold">#{p.orden}</td>
                                    <td className="px-3 py-2 text-xs">{p.concepto}</td>
                                    <td className="px-3 py-2 text-right">{fmtMXN(p.monto_mxn)}</td>
                                    <td className="px-3 py-2 text-center">
                                        {p.pagado ? (
                                            <Badge className="bg-emerald-100 text-emerald-900 border-emerald-300 border">
                                                ✓ {fmtFecha(p.fecha_pago)}
                                            </Badge>
                                        ) : <Badge variant="outline">Pendiente</Badge>}
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        {!p.pagado && t.estado !== "cerrada" && (
                                            <Button size="sm" variant="outline" onClick={() => pagar(p.orden)}
                                                data-testid={`btn-pagar-${p.orden}`}>
                                                Pagar
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </CardContent>
            </Card>

            {/* Checklist */}
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-base">
                        Checklist de extinción
                        <span className="text-sm text-muted-foreground font-normal ml-2">
                            {chkCompletos}/{t.checklist_terminacion.length}
                        </span>
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <ul className="space-y-2" data-testid="checklist-terminacion">
                        {t.checklist_terminacion.map((c, idx) => (
                            <li key={idx} className="flex items-center gap-3 p-2 rounded hover:bg-muted/20">
                                <Button size="sm"
                                    variant={c.completo ? "default" : "outline"}
                                    onClick={() => toggleChk(idx, c.completo)}
                                    disabled={t.estado === "cerrada"}
                                    className={c.completo ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                                    data-testid={`chk-ter-${idx}`}>
                                    <Check className="h-4 w-4" />
                                </Button>
                                <span className="flex-1 text-sm">{c.item}</span>
                            </li>
                        ))}
                    </ul>
                </CardContent>
            </Card>
        </div>
    );
}

export default TerminacionesList;
